import { AlertActionLink, AlertVariant, Button } from '@patternfly/react-core';
import ExternalLinkAltIcon from '@patternfly/react-icons/dist/esm/icons/external-link-alt-icon';
import React, { Dispatch, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-mobx-router5';
import { AlertGroupProvider, useFeature } from '@ole-ui/ole-ui-components';

import { LandingLayout } from 'components/Landing/LandingLayout';
import { unenrollLiveClass } from 'services/LiveClassService';
import useMobxStores from 'hooks/useMobxStores';
import { extractFromSlug } from 'helpers/utils';
import { EnablementFeatureFlags } from 'config/featureFlags';
import VersionsDropdown from 'components/Versions/Dropdown';
import getBundledCourseLaunchUrl from 'helpers/bundledCourse';
import { CourseLandingTab } from './types';
import useCurrentLiveClass from './hooks/useCurrentLiveClass';
import useIsClassOpen from './hooks/useIsClassOpen';

const bundleLaunchExternalIcon = (
  <ExternalLinkAltIcon
    style={{ marginInlineStart: 'var(--pf-v5-global--spacer--sm)' }}
  />
);

type SidebarButtonsProps = {
  activeTab?: CourseLandingTab;
  courseProgress?: number;
  courseSlug?: string;
  currentVersion?: string;
  fetchOverviewData: (code: string, version: string) => Promise<void>;
  hasPremiumSessions: boolean;
  isEnrolling: boolean;
  isInaccessible: boolean;
  onBulkScheduleButton: () => void;
  onCancelBulkScheduling: () => void;
  selectionMode: boolean;
  selectedPremiumSessions: Set<string>;
  selectedVersion: string;
  setIsModalOpen: Dispatch<React.SetStateAction<boolean>>;
  setSelectedVersion: Dispatch<React.SetStateAction<string>>;
  totalSessions: number;
  versions?: string[];
  isBundleCourse?: boolean;
  bundleReference?: string;
  bundleSource?: string;
  onBundleLaunchComplete?: () => void | Promise<void>;
};

const SidebarButtons = ({
  activeTab,
  courseProgress,
  courseSlug,
  currentVersion,
  fetchOverviewData,
  hasPremiumSessions,
  isEnrolling,
  isInaccessible,
  onBulkScheduleButton,
  onCancelBulkScheduling,
  selectionMode,
  selectedPremiumSessions,
  selectedVersion,
  setIsModalOpen,
  setSelectedVersion,
  totalSessions,
  versions,
  isBundleCourse,
  bundleReference,
  bundleSource,
  onBundleLaunchComplete,
}: SidebarButtonsProps) => {
  const { t } = useTranslation();
  const { classesStore, liveClassStore, routerStore, userStore } =
    useMobxStores();

  const isLiveClassEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.LiveClassEnabled,
    defaultFlagValue: false,
  });

  const isSchedulingDesabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.ShowPremvtScheduleDisabled,
    defaultFlagValue: false,
  });

  const isLiveClassButtonEnabled = isLiveClassEnabled;

  const { addAlert, removeAlert } = AlertGroupProvider.useAlertGroupContext();
  const hasLiveClassStarted = useIsClassOpen(
    60, // open N minutes before start
  );
  const {
    canModifyLiveClass,
    currentLiveClassInfo: {
      classUUID,
      hasScheduledLiveClass,
      hasUUID,
      offeringId,
      offeringName,
      startTimeFormatted,
    },
  } = useCurrentLiveClass(courseSlug);

  const { code } = extractFromSlug(courseSlug);

  const hasLiveClassEnrollmentQuota = liveClassStore.enrolledQuotaAllowed > 0;
  const showViewScheduleOnly = isInaccessible || !hasLiveClassEnrollmentQuota;

  const handleCancelClass = useCallback(
    async (id: string) => {
      try {
        await unenrollLiveClass(id);
        removeAlert(`cancel-class-${id}`);
        await liveClassStore.getOfferingEnrolledLiveClass(courseSlug);
        await userStore.getEnablements();
        await classesStore.getClassesForCurrentUser();
        if (selectedVersion) {
          await fetchOverviewData(code, selectedVersion);
        }
      } catch (err: any) {
        removeAlert(`cancel-class-${id}`);

        addAlert({
          title: t('Could not cancel class'),
          children: t(
            "Live class scheduled couldn't be canceled, please try again. If the problem persists please contact support.",
          ),
          variant: AlertVariant.danger,
          className: LandingLayout.alertClassName,
          key: `cancel-fail-${id}`,
          actionLinks: (
            <AlertActionLink
              variant="primary"
              onClick={() => removeAlert(`cancel-fail-${id}`)}
            >
              {t('Ok')}
            </AlertActionLink>
          ),
        });
      }
    },
    [
      removeAlert,
      addAlert,
      t,
      liveClassStore,
      courseSlug,
      selectedVersion,
      fetchOverviewData,
    ],
  );

  const onSelectVersion = useCallback(
    (value: string | number | undefined) => {
      setSelectedVersion(value as string);

      const params: any = { course: `${code}-${value}` };
      if (activeTab !== CourseLandingTab.Overview) {
        params.tab = activeTab;
      }

      routerStore.navigate('courses:overview', params);
    },
    [code, activeTab, routerStore, setSelectedVersion],
  );

  const launchProgress = courseProgress ?? 0;
  const isContinue = launchProgress > 0;

  const standardLaunchTitle = isContinue
    ? t('Continue the course where you left off.')
    : t('Open the course in the learning environment.');

  const handleBundleLaunch = useCallback(() => {
    if (!bundleReference) return;
    const url = getBundledCourseLaunchUrl(bundleReference, bundleSource);
    const win = window.open(url, '_blank');
    win?.focus();
    onBundleLaunchComplete?.();
  }, [bundleReference, bundleSource, onBundleLaunchComplete]);

  if (activeTab === CourseLandingTab.LiveClass && !!courseSlug) {
    if (hasScheduledLiveClass) {
      return (
        <>
          <Link
            href
            {...(hasUUID && hasLiveClassStarted
              ? { routeName: 'classes:view', routeParams: { uuid: classUUID } }
              : {})}
            className={LandingLayout.sidebarLinkClassName(
              hasLiveClassStarted && hasUUID
                ? 'access-class-active'
                : 'access-class-disabled',
            )}
          >
            <LandingLayout.SidebarButton
              isBlock
              isDisabled={!hasLiveClassStarted || !hasUUID}
              title={
                !hasUUID ? t('Attend class') : t('Class link unavailable yet')
              }
              data-analytics-id="attend-class-btn-vt-overview-view-lp"
            >
              {t('Attend class')}
            </LandingLayout.SidebarButton>
          </Link>

          <Link
            href
            routeName="liveclass:schedule"
            routeParams={{ offeringslug: courseSlug }}
            className={LandingLayout.sidebarLinkClassName(
              canModifyLiveClass ? 'reschedule-active' : 'reschedule-disabled',
            )}
          >
            <LandingLayout.SidebarButton
              variant="secondary"
              isBlock
              isDisabled={!canModifyLiveClass}
              data-analytics-id="reschedule-class-btn-vt-overview-view-lp"
            >
              {t('Reschedule')}
            </LandingLayout.SidebarButton>
          </Link>

          <LandingLayout.SidebarButton
            isCancel
            variant="tertiary"
            isBlock
            isDisabled={!canModifyLiveClass}
            onClick={() =>
              addAlert({
                title: t('Cancel this class?'),
                children: t(
                  'You are about to cancel your booking to "{{offeringName}}" scheduled for {{startTimeFormatted}}',
                  {
                    offeringName,
                    startTimeFormatted,
                  },
                ),
                variant: AlertVariant.warning,
                key: `cancel-class-${offeringId}`,
                className: LandingLayout.alertClassName,
                actionLinks: (
                  <>
                    <AlertActionLink
                      onClick={() => handleCancelClass(offeringId)}
                      variant="primary"
                    >
                      {t('Cancel class')}
                    </AlertActionLink>
                    <AlertActionLink
                      variant="link"
                      onClick={() => removeAlert(`cancel-class-${offeringId}`)}
                    >
                      {t('Keep class')}
                    </AlertActionLink>
                  </>
                ),
              })
            }
            data-analytics-id="cancel-class-btn-vt-overview-view-lp"
          >
            {t('Cancel')}
          </LandingLayout.SidebarButton>
        </>
      );
    }

    return (
      <Link
        href
        isDisabled={!isLiveClassButtonEnabled}
        {...(isLiveClassButtonEnabled
          ? {
              routeName: 'liveclass:schedule',
              routeParams: { offeringslug: courseSlug },
            }
          : {})}
      >
        <LandingLayout.SidebarButton
          isBlock
          data-analytics-id="schedule-class-btn-vt-overview-view-lp"
        >
          {showViewScheduleOnly ? t('View Schedule') : t('Schedule')}
        </LandingLayout.SidebarButton>
      </Link>
    );
  }

  if (hasPremiumSessions && activeTab === CourseLandingTab.PremiumSessions) {
    if (selectionMode) {
      const selectedSessions = Array.from(selectedPremiumSessions)?.length;

      return (
        <>
          <span>
            <Trans
              components={{ bold: <b /> }}
              defaults="Scheduling <bold>{{selectedSessions}} out of {{totalSessions}}</bold> live sessions:"
              values={{ selectedSessions, totalSessions }}
            />
          </span>

          <Button
            variant="primary"
            isLoading={isEnrolling}
            isDisabled={
              isEnrolling ||
              selectedPremiumSessions.size === 0 ||
              isSchedulingDesabled
            }
            onClick={() => setIsModalOpen(true)}
            data-analytics-id="confirm-schedule-btn-vt-overview-view-lp"
          >
            {t('Confirm')}
          </Button>

          <Button
            variant="secondary"
            onClick={onCancelBulkScheduling}
            data-analytics-id="cancel-bulk-schedule-btn-vt-overview-view-lp"
          >
            {t('Cancel bulk schedule')}
          </Button>
        </>
      );
    }

    return (
      <LandingLayout.SidebarButton
        variant="primary"
        aria-disabled={selectionMode}
        isDisabled={isSchedulingDesabled}
        onClick={onBulkScheduleButton}
        data-analytics-id="bulk-schedule-btn-vt-overview-view-lp"
      >
        {t('Bulk schedule')}
      </LandingLayout.SidebarButton>
    );
  }

  const getPrimaryLaunchButton = (): React.ReactNode => {
    if (!courseSlug) return null;

    // standard course
    if (!isBundleCourse) {
      return (
        <Link
          href
          {...(!isInaccessible
            ? {
                routeName: 'courses:view',
                routeParams: { course: courseSlug },
              }
            : {})}
          isDisabled={isInaccessible}
        >
          <LandingLayout.SidebarButton
            isBlock
            isDisabled={isInaccessible}
            title={standardLaunchTitle}
            data-analytics-id={
              isContinue
                ? 'continue-btn-course-overview-lp'
                : 'launch-btn-course-overview-lp'
            }
          >
            {isContinue ? t('Continue') : t('Launch')}
          </LandingLayout.SidebarButton>
        </Link>
      );
    }

    // bundled course
    if (bundleReference) {
      return (
        <LandingLayout.SidebarButton
          isBlock
          isDisabled={isInaccessible}
          onClick={handleBundleLaunch}
          icon={bundleLaunchExternalIcon}
          iconPosition="end"
          title={t('Opens the bundled course package in a new browser tab.')}
          data-analytics-id={
            isContinue
              ? 'continue-bundle-btn-course-overview-lp'
              : 'launch-bundle-btn-course-overview-lp'
          }
        >
          {isContinue ? t('Continue') : t('Launch')}
        </LandingLayout.SidebarButton>
      );
    }

    // no course available (fallback should never happen in production)
    return (
      <LandingLayout.SidebarButton
        isBlock
        isDisabled
        icon={bundleLaunchExternalIcon}
        iconPosition="end"
        title={t('Course launch is not available.')}
      >
        {t('Launch')}
      </LandingLayout.SidebarButton>
    );
  };

  const primaryLaunchButton = getPrimaryLaunchButton();

  return (
    <>
      {primaryLaunchButton}

      <LandingLayout.SidebarActions>
        {!!versions?.length && (
          <VersionsDropdown
            code={code}
            currentVersion={currentVersion}
            onOptionSelect={onSelectVersion}
            versions={versions}
            displayTooltip={false}
          />
        )}
      </LandingLayout.SidebarActions>
    </>
  );
};

export default SidebarButtons;
